/**
 * UndoToast (PHASE1 §7.2). A transient bottom bar that appears for five seconds
 * after each inbox action, offering a one-tap "Rückgängig". It re-surfaces on
 * every new action (keyed on `action.seq`); the `u` hotkey in `InboxPage`
 * offers the same LIFO undo for the whole session regardless of the timer.
 */
import { useEffect, useState } from 'react';
import { Undo2, X } from 'lucide-react';
import type { InboxLastAction } from '../../lib/hooks/useInbox.ts';

const VISIBLE_MS = 5000;

export interface UndoToastProps {
  action: InboxLastAction | null;
  canUndo: boolean;
  onUndo: () => void;
}

export function UndoToast({ action, canUndo, onUndo }: UndoToastProps) {
  // `hiddenSeq` is the action seq the user dismissed (or that timed out).
  // Visibility is derived so no synchronous setState is needed on a new action.
  const [hiddenSeq, setHiddenSeq] = useState(0);
  const seq = action?.seq ?? 0;
  const visible = action !== null && seq !== 0 && seq !== hiddenSeq;

  useEffect(() => {
    if (seq === 0) return;
    const timer = window.setTimeout(() => setHiddenSeq(seq), VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [seq]);

  if (!visible || !action) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 sm:bottom-6">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-white px-4 py-2 text-sm shadow-lg ring-1 ring-slate-900/5 dark:bg-slate-900 dark:ring-white/10">
        <span className="text-slate-600 dark:text-slate-300">{action.label}</span>
        <button
          type="button"
          disabled={!canUndo}
          onClick={() => {
            onUndo();
            setHiddenSeq(seq);
          }}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 font-semibold text-sky-600 transition-colors duration-150 hover:bg-sky-50 disabled:opacity-40 dark:text-sky-400 dark:hover:bg-sky-950"
        >
          <Undo2 className="h-4 w-4" aria-hidden />
          Rückgängig
        </button>
        <button
          type="button"
          onClick={() => setHiddenSeq(seq)}
          aria-label="Schließen"
          className="rounded-full p-1 text-slate-400 transition-colors duration-150 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
